// "Needs re-planning" — the bookings an unavailability landed on top of.
//
// Spec 05 R6: when an unavailability (or a status drop) covers existing
// bookings, those bookings are flagged rather than deleted — reality first —
// and the office sorts them out. The worker's app shows the booking normally
// until it actually changes, so this panel is the only place the problem
// surfaces, which is why it sits above the board rather than inside a tab.
//
// Zanetto Tools has an equivalent panel; this is built against Dayboard's own
// `needsReplanning()` endpoint, which already existed from phase 2 and was
// simply never shown on the schedule page.
import { dateRangeLabel } from '../../lib/dates';
import { bookingLabel } from '../../lib/bookingLabel';
import type { CalendarBooking } from '../../schedulingApi';

/**
 * Exactly a calendar booking — the endpoint answers with the same enriched row
 * the calendar does.
 *
 * It used to carry only the four fields this panel draws, which meant "Sort it
 * out" could not open the editor from what it already had and went looking for
 * the booking in the window on screen instead. The board moved to go and find
 * it. The name stays because it is what the panel's items are called.
 *
 * The job fields are null on a maintenance booking (migration 013), which gets
 * flagged for re-planning exactly like a job booking does.
 */
export type ReplanningItem = CalendarBooking;

export function ReplanningPanel({
  items,
  onFix,
}: {
  items: ReplanningItem[];
  onFix: (booking: ReplanningItem) => void;
}) {
  if (items.length === 0) return null;

  return (
    <section
      className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3"
      // Not role="alert": this is a standing state, not something that just
      // happened, and an assertive live region would interrupt a screen reader
      // every time the board reloads.
      aria-labelledby="replanning-heading"
    >
      <h2 id="replanning-heading" className="!mb-1 text-sm font-semibold text-red-800">
        {items.length === 1
          ? 'One booking needs re-planning'
          : `${items.length} bookings need re-planning`}
      </h2>
      <p className="!mb-2 text-sm text-red-800">
        Something became unavailable over the top of these. Nothing has been cancelled: move them,
        release them, or remove the unavailability.
      </p>

      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id} className="flex flex-wrap items-baseline gap-x-2 text-sm text-red-900">
            <span className="font-medium">{item.resourceName}</span>
            <span>on {bookingLabel(item)}</span>
            <span className="text-red-700">
              {dateRangeLabel(item.startDate, item.endDate)}
            </span>
            <button
              type="button"
              onClick={() => onFix(item)}
              className="row-action-danger ml-auto"
            >
              Sort it out
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
